<script setup lang="ts">
import { CircleAlert, CircleCheck, Info, TriangleAlert, X } from 'lucide-vue-next'
import { ToastClose, ToastDescription, ToastProvider, ToastRoot, ToastTitle, ToastViewport } from 'reka-ui'
import type { FunctionalComponent } from 'vue'

import { cn, ICON_STROKE, TONE_SURFACE } from './classes'
import type { Tone } from './types'
import { useToast } from './useToast'

/**
 * Miesto, kde sa toasty vykresľujú. Patrí do shellu, nie do stránok.
 *
 * Reka rieši `role="status"`, pauzu pri hoveri a klávesovú skratku F8 na
 * presun fokusu do zoznamu toastov.
 */
const { items, dismiss } = useToast()

const ICON: Readonly<Record<Tone, FunctionalComponent>> = {
	neutral: Info,
	brand: Info,
	info: Info,
	success: CircleCheck,
	warning: TriangleAlert,
	danger: CircleAlert,
}
</script>

<template>
	<ToastProvider :duration="5000" swipe-direction="right" label="Oznámenia">
		<ToastRoot
			v-for="item in items"
			:key="item.id"
			:duration="item.durationMs === 0 ? Number.POSITIVE_INFINITY : item.durationMs"
			:class="
				cn(
					'flex items-start gap-2 rounded-lg border p-3 shadow-md',
					'transition-opacity duration-(--motion-base) ease-out',
					TONE_SURFACE[item.tone],
				)
			"
			@update:open="(open: boolean) => !open && dismiss(item.id)"
		>
			<component :is="ICON[item.tone]" :size="20" :stroke-width="ICON_STROKE" class="mt-px shrink-0" aria-hidden="true" />
			<div class="flex min-w-0 flex-1 flex-col gap-0.5">
				<ToastTitle class="text-base leading-tight font-medium">{{ item.titleSk }}</ToastTitle>
				<ToastDescription v-if="item.descriptionSk !== undefined" class="text-sm">
					{{ item.descriptionSk }}
				</ToastDescription>
			</div>
			<ToastClose class="shrink-0 rounded-sm p-0.5 focus-visible:focus-ring" aria-label="Zavrieť oznámenie">
				<X :size="16" :stroke-width="ICON_STROKE" aria-hidden="true" />
			</ToastClose>
		</ToastRoot>

		<ToastViewport
			class="fixed right-4 bottom-4 z-(--z-toast) flex w-[calc(100vw-32px)] max-w-(--panel-w-narrow) flex-col gap-2 outline-none"
		/>
	</ToastProvider>
</template>
